/**
 * "Workspace Members" modal (Figma 10219:25150), opened from a discover card.
 * A 600px Spark Modal: "{n} Members" + "Search by name or email" on top, then
 * 48px rows (large Avatar with name / email, role TagColored at the right),
 * and a Close footer.  Loads `GET /workspaces/discover/{id}/members` each
 * time it opens.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { EmptyState, Modal, Search, Skeleton, SparkModalPortal, TagColored, Text } from '@spark';
import { fetchDiscoverMembers } from '../../api/discoverApi';
import { displayRole } from '../../utils/discoverFilters';
import { PersonAvatar } from './MemberAvatarStack';
import { DISCOVER_ROLE_TAG_VARIANT } from './WorkspaceDiscoverCard';

export const MEMBERS_MODAL_TITLE = 'Workspace Members';
export const MEMBERS_MODAL_WIDTH = 600;
export const MEMBERS_SEARCH_PLACEHOLDER = 'Search by name or email';

export const membersCountLabel = (count) => `${count} ${count === 1 ? 'Member' : 'Members'}`;

const SKELETON_ROWS = 5;

function MemberRow({ member }) {
    const role = displayRole(member) || 'Editor';
    const people = { [member.email]: { name: member.name, picture: member.picture } };
    return (
        <li className="discover-members__row" data-testid="discover-member-row">
            <div className="discover-members__person">
                <PersonAvatar email={member.email} people={people} size="large" />
                <div className="discover-members__text">
                    <Text as="p" variant="base-semibold" className="discover-members__name">
                        {member.name || member.email}
                    </Text>
                    {member.name ? (
                        <Text as="p" variant="detail-regular" color="secondary" className="discover-members__email">
                            {member.email}
                        </Text>
                    ) : null}
                </div>
            </div>
            <TagColored variant={DISCOVER_ROLE_TAG_VARIANT[role] || 'yellow'} label={role} />
        </li>
    );
}

export default function WorkspaceMembersModal({ open, workspace, onClose }) {
    const workspaceId = workspace?.id || null;
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!open || !workspaceId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError('');
        setMembers([]);
        setQuery('');
        fetchDiscoverMembers(workspaceId).then((result) => {
            if (cancelled) return;
            if (result.ok) {
                setMembers(Array.isArray(result.data?.members) ? result.data.members : []);
            } else {
                setError(result.error);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [open, workspaceId, attempt]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return members;
        return members.filter(
            (member) => String(member.name || '').toLowerCase().includes(needle)
                || String(member.email || '').toLowerCase().includes(needle),
        );
    }, [members, query]);

    let body;
    if (loading) {
        body = (
            <ul className="discover-members__list" aria-busy="true" data-testid="discover-members-loading">
                {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <li key={index} className="discover-members__row discover-members__row--skeleton">
                        <Skeleton shapeType="circle" width={48} height={48} />
                        <div className="discover-members__text">
                            <Skeleton width={140} height={14} />
                            <Skeleton width={200} height={12} />
                        </div>
                    </li>
                ))}
            </ul>
        );
    } else if (error) {
        body = (
            <EmptyState
                size="small"
                illustration="illustration-feedback-face-sad-outline"
                title="Couldn't load members"
                subtitle={error}
                showMainButton
                ctaLabel="Try again"
                showCtaIcon={false}
                onCtaClick={() => setAttempt((n) => n + 1)}
            />
        );
    } else if (visible.length === 0) {
        body = (
            <EmptyState
                size="small"
                illustration="illustration-no-results"
                title={members.length === 0 ? 'No members yet' : 'No members match your search'}
                showSubtitle={false}
                showMainButton={false}
            />
        );
    } else {
        body = (
            <ul className="discover-members__list" data-testid="discover-members-list">
                {visible.map((member) => (
                    <MemberRow key={member.email} member={member} />
                ))}
            </ul>
        );
    }

    return (
        <SparkModalPortal
            open={Boolean(open && workspaceId)}
            onClose={() => onClose?.()}
            width={MEMBERS_MODAL_WIDTH}
            className="discover-members-portal"
            destroyOnClose
        >
            <Modal
                open
                className="discover-members"
                title={MEMBERS_MODAL_TITLE}
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                showMainButton={false}
                tertiaryLabel="Close"
                onTertiaryClick={() => onClose?.()}
                onCloseClick={() => onClose?.()}
            >
                <div className="discover-members__content" data-testid="workspace-members-modal">
                    <div className="discover-members__top">
                        <Text as="p" variant="base-regular" color="secondary" data-testid="discover-members-count">
                            {loading ? 'Loading members' : membersCountLabel(members.length)}
                        </Text>
                        <div className="discover-members__search">
                            <Search
                                size="medium"
                                iconPosition="start"
                                placeholder={MEMBERS_SEARCH_PLACEHOLDER}
                                aria-label={MEMBERS_SEARCH_PLACEHOLDER}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                showClearButton
                                onClear={() => setQuery('')}
                                disabled={loading || Boolean(error)}
                                data-testid="discover-members-search"
                            />
                        </div>
                    </div>
                    {body}
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
